import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useCurrentWeek } from '../context/CurrentWeekContext';
import styles from '../styles/CalendarStatus.module.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const CalendarStatus = () => {
  const [allCalendarData, setAllCalendarData] = useState([]);
  const [filteredCalendar, setFilteredCalendar] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState(null);
  const { calendar, isLoading: contextLoading, error: contextError, fetchCurrentWeekAndCalendar } = useCurrentWeek();

  const fetchCalendar = useCallback(async () => {
    try {
      await fetchCurrentWeekAndCalendar();
    } catch (err) {
      console.error(err);
      setError('Failed to fetch calendar data');
    }
  }, [fetchCurrentWeekAndCalendar]);

  useEffect(() => {
    fetchCalendar();
  }, [fetchCalendar]);

  useEffect(() => {
    if (calendar && !contextLoading) {
      setAllCalendarData(calendar);
      setLocalLoading(false);
    }
  }, [calendar, contextLoading]);

  useEffect(() => {
    if (allCalendarData.length > 0) {
      const uniqueYears = [...new Set(allCalendarData.map(week => week.season))];
      setYears(uniqueYears.sort((a, b) => b - a));
      setSelectedYear(uniqueYears[0]);
    }
  }, [allCalendarData]);

  const filterCalendarData = useCallback((year) => {
    const filtered = allCalendarData
      .filter(week => week.season === parseInt(year))
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
    setFilteredCalendar(filtered);
  }, [allCalendarData]);

  useEffect(() => {
    if (selectedYear) {
      filterCalendarData(selectedYear);
    }
  }, [selectedYear, filterCalendarData]);

  const updateBowlWeek = async (newEndDate, weekData) => {
    try {
      await api.put('/calendar/update-bowl-week', {
        season: selectedYear,
        week: weekData.week,
        newEndDate: newEndDate.toISOString()
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to update bowl week');
    }
  };

  const updateHideWeek = async (hideWeek, weekData) => {
    try {
      await api.put('/calendar/update-hide-week', {
        season: selectedYear,
        week: weekData.week,
        seasonType: weekData.season_type,
        hideWeek
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to update hide week status');
    }
  };

  const updateNotes = async (notes, weekData) => {
    try {
      await api.put('/calendar/update-notes', {
        season: selectedYear,
        week: weekData.week,
        notes
      });
      setAllCalendarData(prevData => 
        prevData.map(week => 
          week.season === selectedYear && week.week === weekData.week
            ? { ...week, notes }
            : week
        )
      );
    } catch (err) {
      setError('Failed to update notes');
    }
  };

  // New function to update max picks
  const updateMaxPicks = async (maxPicks, weekData) => {
    try {
      await api.put('/calendar/update-max-picks', {
        season: selectedYear,
        week: weekData.week,
        maxPicks: parseInt(maxPicks)
      });
      setAllCalendarData(prevData => 
        prevData.map(week => 
          week.season === selectedYear && week.week === weekData.week
            ? { ...week, max_picks: parseInt(maxPicks) }
            : week
        )
      );
    } catch (err) {
      setError('Failed to update max picks');
    }
  };

  // New function to create a custom week
  const createCustomWeek = async () => {
    const lastWeek = filteredCalendar[filteredCalendar.length - 1];
    const newStartDate = new Date(lastWeek.end_date);
    newStartDate.setMinutes(newStartDate.getMinutes() + 1);
    
    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newEndDate.getDate() + 7);

    try {
      await api.post('/calendar/create-week', {
        season: selectedYear,
        week: lastWeek.week + 1,
        startDate: newStartDate.toISOString(),
        endDate: newEndDate.toISOString(),
        notes: 'Custom Week',
        maxPicks: 3
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to create custom week');
    }
  };

  // New function to delete a custom week
  const deleteCustomWeek = async (weekData) => {
    if (!weekData.user_added) {
      setError('Only custom weeks can be deleted');
      return;
    }

    try {
      await api.delete('/calendar/delete-week', {
        data: {
          season: selectedYear,
          week: weekData.week
        }
      });
      fetchCalendar();
    } catch (err) {
      setError('Failed to delete week');
    }
  };

  const handleNotesChange = (e, weekData) => {
    setFilteredCalendar(prevCalendar => 
      prevCalendar.map(week => 
        week.week === weekData.week && week.season_type === weekData.season_type
          ? { ...week, notes: e.target.value }
          : week
      )
    );
  };

  if (localLoading || contextLoading) return <div>Loading calendar status...</div>;
  if (error || contextError) return <div>{error || contextError}</div>;
  if (allCalendarData.length === 0) return <div>No calendar data available.</div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>Calendar Status</h3>
        <select 
          className={styles.yearSelect}
          value={selectedYear} 
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>
      <table className={styles.statusTable}>
        <thead>
          <tr>
            <th className={styles.weekColumn}>Week</th>
            <th className={styles.notesColumn}>Notes</th>
            <th className={styles.dateColumn}>Start Date</th>
            <th className={styles.dateColumn}>End Date</th>
            <th className={styles.countColumn}>Games</th>
            <th className={styles.picksColumn}>Picks</th>
            <th className={styles.checkboxColumn}>Hide</th>
            <th className={styles.checkboxColumn}>Custom</th>
            <th className={styles.actionColumn}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredCalendar.map((week) => (
            <tr key={`${week.season_type}-${week.week}`}>
              <td className={styles.weekColumn}>{week.season_type === 'postseason' ? 'Post' : `Week ${week.week}`}</td>
              <td className={styles.notesColumn}>
                <input 
                  type="text" 
                  value={week.notes || ''} 
                  onChange={(e) => handleNotesChange(e, week)}
                  onBlur={() => updateNotes(week.notes, week)}
                />
              </td>
              <td className={styles.dateColumn}>
                <DatePicker
                  selected={new Date(week.start_date)}
                  onChange={(date) => updateBowlWeek(date, week)}
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  disabled={!week.user_added}
                />
              </td>
              <td className={styles.dateColumn}>
                <DatePicker
                  selected={new Date(week.end_date)}
                  onChange={(date) => updateBowlWeek(date, week)}
                  showTimeSelect
                  dateFormat="MMM d, yyyy h:mm aa"
                  disabled={!week.user_added}
                />
              </td>
              <td className={styles.countColumn}>{week.gameCount}</td>
              <td className={styles.picksColumn}>
                <input 
                  type="number" 
                  min="1"
                  max="10"
                  value={week.max_picks || 1} 
                  onChange={(e) => updateMaxPicks(e.target.value, week)}
                  style={{ width: '50px' }}
                />
              </td>
              <td className={styles.checkboxColumn}>
                <input 
                  type="checkbox" 
                  checked={week.hide_week === 1} 
                  onChange={(e) => updateHideWeek(e.target.checked, week)}
                />
              </td>
              <td className={styles.checkboxColumn}>
                <input 
                  type="checkbox" 
                  checked={week.user_added === 1} 
                  disabled
                />
              </td>
              <td className={styles.actionColumn}>
                {week.user_added === 1 && (
                  <button 
                    className={styles.deleteButton}
                    onClick={() => deleteCustomWeek(week)}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button 
        className={styles.createButton}
        onClick={createCustomWeek}
      >
        Add Custom Week
      </button>
    </div>
  );
};

export default CalendarStatus;
